/* eslint-disable import/no-extraneous-dependencies */
/**
 * Создание корневого store с единой цепочкой middleware (applyMiddleware).
 * @module store/createStore
 */

import { create } from 'zustand';
import { redux } from 'zustand/middleware';

import { appReducer, initialAppState } from './app';
import { applyMiddleware } from './applyMiddleware';
import { initialProfileState, profileReducer } from './profile';
import { fetchProfileMiddleware } from './profile/fetchProfile.middleware';
import { initialRegistrationSurveyState, registrationSurveyReducer } from './registrationSurvey';
import { fetchRegistrationSurveyMiddleware } from './registrationSurvey/fetchRegistrationSurvey.middleware';
import type { MiddlewareAPI, RootAction, RootDispatch, RootState, StoreAPI } from './store.types';

/** Начальное состояние корневого store */
const rootInitialState: RootState = {
  app: initialAppState,
  profile: initialProfileState,
  registrationSurvey: initialRegistrationSurveyState,
};

/**
 * Корневой редьюсер: делегирует слайсам по типу экшена.
 *
 * @param {RootState} state — полное состояние
 * @param {RootAction} action — экшен
 * @returns {RootState}
 */
function rootReducer(state: RootState, action: RootAction): RootState {
  return {
    app: appReducer(state.app, action),
    profile: profileReducer(state.profile, action),
    registrationSurvey: registrationSurveyReducer(state.registrationSurvey, action),
  };
}

const store = create(redux<RootState, RootAction>(rootReducer, rootInitialState));

const rawDispatch = store.dispatch as RootDispatch;

const api: StoreAPI<RootState, RootDispatch> = {
  getState: store.getState,
  dispatch: null as unknown as RootDispatch,
};

const dispatch = applyMiddleware<RootState, RootDispatch>(
  api as MiddlewareAPI<RootDispatch, RootState>,
  rawDispatch,
  fetchProfileMiddleware,
  fetchRegistrationSurveyMiddleware,
);

api.dispatch = dispatch;

(store as { dispatch: RootDispatch }).dispatch = dispatch;

export const rootStore = store as typeof store & {
  getState: () => RootState;
  dispatch: RootDispatch;
  subscribe: typeof store.subscribe;
};
